// Close-code classification for Colyseus disconnects.
// Ported from MillionDollarMoneyDrop's connectionManager, with corrections:
// - 4002 is Colyseus CloseCode.WITH_ERROR (server hit an error handling this
//   session), NOT a GamesServer domain code — the GamesServer never emits 4002.
//   It is transient, so it must be retryable.
// - 4010 (CloseCode.MAY_TRY_RECONNECT) added as retryable.
// - Codes the GamesServer actually emits (4008/4009/4100/4200) added as terminal.
export const CloseCodes = {
    // --- Standard WebSocket layer ---
    1001: { displayMessage: "Browser Tab Closing", retryable: false },
    1002: { displayMessage: "Malformed frame received", retryable: false },
    1003: { displayMessage: "Unsupported frame received", retryable: false },
    1006: { displayMessage: "Ungraceful shutdown / connection refused", retryable: true, retryDelay: 2500 },
    1011: { displayMessage: "Internal Error", retryable: false },
    1012: { displayMessage: "Server Restarting", retryable: true, retryDelay: 4000 },
    1013: { displayMessage: "Try again later, server capacity full", retryable: true, retryDelay: 5000 },
    1014: { displayMessage: "Bad Gateway", retryable: true, retryDelay: 4000 },
    // --- Colyseus protocol codes ---
    4000: { displayMessage: "Left the room.", retryable: false }, // CloseCode.CONSENTED
    4001: { displayMessage: "Room has been closed.", retryable: false }, // GamesServer inactivity close (also Colyseus SERVER_SHUTDOWN)
    4002: { displayMessage: "Connection error, retrying...", retryable: true, retryDelay: 2000 }, // CloseCode.WITH_ERROR — transient
    4003: { displayMessage: "Failed to reconnect.", retryable: false }, // CloseCode.FAILED_TO_RECONNECT
    4010: { displayMessage: "Reconnecting...", retryable: true, retryDelay: 2000 }, // CloseCode.MAY_TRY_RECONNECT
    // --- GamesServer domain codes ---
    4008: { displayMessage: "Connect disabled by host.", retryable: false },
    4009: { displayMessage: "Removed by host.", retryable: false },
    4050: { displayMessage: "Player removed.", retryable: false },
    4100: { displayMessage: "External game ended.", retryable: false },
    4200: { displayMessage: "Session is no longer valid.", retryable: false },
};

// Default fallback if code not mapped
const DEFAULT_REASON = "Oops! Looks like something went wrong.";

// -------------------- Helpers --------------------

/** Is this code terminal? (don't retry) */
export function isTerminalByCode(code) {
    return !CloseCodes[code]?.retryable;
}

/** Retry delay (ms) if retryable */
export function retryDelayForCode(code) {
    return CloseCodes[code]?.retryDelay || 2000;
}

/** Human-friendly text for UI */
export function getCloseReasonText(code, fallbackMsg) {
    if (CloseCodes[code]?.displayMessage) return CloseCodes[code].displayMessage;
    return fallbackMsg || DEFAULT_REASON;
}

/** Regex-based heuristic for Colyseus reconnect errors (string messages, not codes) */
export function isTerminalReconnectError(err) {
    // Check if it's a ProgressEvent (XMLHttpRequest error from failed HTTP request)
    // This happens when server is unreachable/crashed during matchmaking/reconnection
    if (err instanceof ProgressEvent && err.type === "error") {
        return true;
    }

    const msg = `${err?.message || ""}`.toLowerCase();

    // Check for connection refused errors (server crashed/unreachable)
    if (/err_connection_refused|connection\s*refused|econnrefused/.test(msg)) {
        return true;
    }

    // Check for room-related terminal errors
    return /not\s*found|room\s*not\s*found|disposed|room\s*disposed|expired|reconnection\s*token|invalid\s*room|invalid\s*reconnection|closed\s*room/.test(msg);
}
